import { useMemo } from 'react';

const MORSE_CODE = {
  'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.',
  'G': '--.', 'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..',
  'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.',
  'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
  'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---',
  '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...',
  '8': '---..', '9': '----.'
};

export default function MorseCodeVisualizer({ text = '', size = 'md' }) {
  const beads = useMemo(() => {
    const result = [];
    const upperText = text.toUpperCase();
    
    for (let i = 0; i < upperText.length; i++) {
      const char = upperText[i];
      
      if (char === ' ') {
        result.push({ type: 'spacer', key: `space-${i}` });
      } else if (MORSE_CODE[char]) {
        const morse = MORSE_CODE[char];
        for (let j = 0; j < morse.length; j++) {
          result.push({
            type: morse[j] === '.' ? 'dot' : 'dash',
            key: `${char}-${i}-${j}`
          });
        }
        // Add small gap between letters (not after last letter)
        if (i < upperText.length - 1 && upperText[i + 1] !== ' ') {
          result.push({ type: 'letter-gap', key: `gap-${i}` });
        }
      }
    }
    
    return result;
  }, [text]);

  const sizeClasses = {
    sm: {
      dot: 'w-2 h-2',
      dash: 'w-4 h-2',
      spacer: 'w-3 h-2',
      gap: 'w-1.5'
    },
    md: {
      dot: 'w-3 h-3',
      dash: 'w-6 h-3',
      spacer: 'w-4 h-3',
      gap: 'w-2'
    },
    lg: {
      dot: 'w-4 h-4',
      dash: 'w-8 h-4',
      spacer: 'w-5 h-4',
      gap: 'w-3'
    }
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  if (!text) {
    return (
      <div className="flex items-center justify-center py-4 text-[#84A98C] text-sm">
        Type something to see your Morse code bracelet
      </div>
    );
  }

  return (
    <div className="relative py-6" data-testid="morse-visualizer">
      {/* String line */}
      <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-[#354F52]/20 -translate-y-1/2" />
      
      {/* Beads container */}
      <div className="relative flex items-center justify-center flex-wrap gap-y-4 px-4">
        {beads.map((bead) => {
          if (bead.type === 'dot') {
            return (
              <div
                key={bead.key}
                className={`${sizes.dot} bg-[#D4AF37] rounded-full mx-0.5 shadow-sm bead-enter`}
                data-testid="morse-dot"
              />
            );
          }
          if (bead.type === 'dash') {
            return (
              <div
                key={bead.key}
                className={`${sizes.dash} bg-[#D4AF37] rounded-full mx-0.5 shadow-sm bead-enter`}
                data-testid="morse-dash"
              />
            );
          }
          if (bead.type === 'spacer') {
            return (
              <div
                key={bead.key}
                className={`${sizes.spacer} bg-[#84A98C] rounded-full mx-1 opacity-50`}
                data-testid="morse-spacer"
              />
            );
          }
          if (bead.type === 'letter-gap') {
            return (
              <div
                key={bead.key}
                className={sizes.gap}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
